import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { getGameSpecials, updateGameSpecials } from "Api/apiService";

const emptyCard = {
  icon: "",
  title: "",
  sub: "",
  bullets: "",
};

const parseBullets = (value) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

export default function GameSpecialsAdmin() {
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [cards, setCards] = useState([emptyCard]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const textColor = useColorModeValue("navy.700", "white");
  const labelColor = useColorModeValue("secondaryGray.600", "whiteAlpha.700");

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await getGameSpecials();
      const payload = response?.data?.gameSpecials;
      setHeroTitle(payload?.heroTitle || "");
      setHeroSubtitle(payload?.heroSubtitle || "");
      if (Array.isArray(payload?.cards) && payload.cards.length > 0) {
        setCards(
          payload.cards.map((card) => ({
            ...card,
            bullets: Array.isArray(card.bullets) ? card.bullets.join("\n") : "",
          }))
        );
      } else {
        setCards([emptyCard]);
      }
    } catch (error) {
      console.error("Failed to load game specials", error);
      toast({
        title: "Unable to load game specials.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleCardChange = (index, key) => (event) => {
    const value = event.target.value;
    setCards((prev) =>
      prev.map((card, idx) => (idx === index ? { ...card, [key]: value } : card))
    );
  };

  const addCard = () => {
    setCards((prev) => [...prev, emptyCard]);
  };

  const removeCard = (index) => {
    setCards((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== index)));
  };

  const handleSave = async () => {
    if (!heroTitle.trim() || !heroSubtitle.trim()) {
      toast({
        title: "Hero title and subtitle are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heroTitle: heroTitle.trim(),
        heroSubtitle: heroSubtitle.trim(),
        cards: cards
          .filter((card) => card.title.trim() && card.sub.trim())
          .map((card) => ({
            icon: card.icon.trim(),
            title: card.title.trim(),
            sub: card.sub.trim(),
            bullets: parseBullets(card.bullets || ""),
          })),
      };
      await updateGameSpecials(payload);
      toast({
        title: "Game specials updated.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadContent();
    } catch (error) {
      console.error("Failed to update game specials", error);
      toast({
        title: "Unable to update game specials.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1 }} spacing="20px">
        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Game Specials Information
            </Text>
            <Text fontSize="14px" color={labelColor}>
              Update the hero and cards shown on the mobile Game Specials screen.
            </Text>
            <Divider />
            <Stack spacing="14px">
              <FormControl>
                <FormLabel color={labelColor}>Hero Title</FormLabel>
                <Input value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>Hero Subtitle</FormLabel>
                <Input
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                />
              </FormControl>
            </Stack>
          </Flex>
        </Card>

        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Flex justify="space-between" align="center">
              <Text fontSize="22px" fontWeight="700" color={textColor}>
                Special Cards
              </Text>
              <Button variant="outline" onClick={addCard}>
                Add Card
              </Button>
            </Flex>
            <Divider />
            <Stack spacing="14px">
              {cards.map((card, index) => (
                <Box
                  key={`card-${index}`}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="16px"
                >
                  <Flex justify="space-between" align="center" mb="12px">
                    <Text fontWeight="700" color={textColor}>
                      Card {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeCard(index)}
                      isDisabled={cards.length === 1}
                    >
                      Remove
                    </Button>
                  </Flex>
                  <Stack spacing="12px">
                    <FormControl>
                      <FormLabel color={labelColor}>Icon (emoji)</FormLabel>
                      <Input value={card.icon} onChange={handleCardChange(index, "icon")} />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Title</FormLabel>
                      <Input value={card.title} onChange={handleCardChange(index, "title")} />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Subtitle</FormLabel>
                      <Input value={card.sub} onChange={handleCardChange(index, "sub")} />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Bullets (one per line)</FormLabel>
                      <Textarea
                        value={card.bullets}
                        onChange={handleCardChange(index, "bullets")}
                        rows={4}
                      />
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Flex>
        </Card>

        <Flex justify="flex-end">
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading}
          >
            Save Changes
          </Button>
        </Flex>
      </SimpleGrid>
    </Box>
  );
}
